import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from 'reactstrap';
import * as Icon from 'react-feather';
import ModalSheet from '../modal-sheet/modal-sheet.component';
import { connect } from 'react-redux';
import {
  setCreateBillingAddressModalData,
  setCreateDeliveryAddressModalData,
  setEditBillingAddressModalData,
  setEditDeliveryAddressModalData,
  setIsCreateBillingAddressModalOpen,
  setIsCreateDeliveryAddressModalOpen,
  setIsEditBillingAddressModalOpen,
  setIsEditDeliveryAddressModalOpen,
  setIsListAddressesModalOpen,
} from '../../../../redux/modal/modal.actions';
import PropTypes from 'prop-types';
import RemoveConfirmationModal from '../remove-confirmation/remove-confirmation.modal';
import { removeAddress as _removeAddress } from './list-addresses.service';
import Empty from '../../../../views/errors/empty.component';
import {
  fetchBillingAddresses,
  fetchDeliveryAddresses,
} from '../../../../redux/me/me.actions';
import { BILLING_ADDRESS } from '../../../../utility/constants';

const ListAddressesModal = ({
  isOpen,
  data, // {type: string} one of: BILLING_ADDRESS or DELIVERY_ADDRESS
  billingAddresses,
  deliveryAddresses,

  setIsListAddressesModalOpen,
  setIsCreateBillingAddressModalOpen,
  setCreateBillingAddressModalData,
  setIsEditBillingAddressModalOpen,
  setEditBillingAddressModalData,
  setIsCreateDeliveryAddressModalOpen,
  setCreateDeliveryAddressModalData,
  setIsEditDeliveryAddressModalOpen,
  setEditDeliveryAddressModalData,
  fetchBillingAddresses,
  fetchDeliveryAddresses,
}) => {
  const intl = useIntl();
  const [removeAddress, setRemoveAddress] = useState(null);

  const closeModal = () => setIsListAddressesModalOpen(false);

  const isBillingAddressMode = data?.type === BILLING_ADDRESS;
  const addresses = isBillingAddressMode ? billingAddresses : deliveryAddresses;

  return (
    <>
      <ModalSheet
        isOpen={isOpen}
        closeModal={closeModal}
        title={intl.formatMessage({
          id: isBillingAddressMode
            ? 'billingAddressesTitle'
            : 'deliveryAddressesTitle',
        })}
        footer={
          <div className="align-content-center">
            <Button
              data-testid="addressesButton"
              color="primary"
              className="w-100"
              onClick={() => {
                if (isBillingAddressMode) {
                  setIsCreateBillingAddressModalOpen(true);
                } else {
                  setIsCreateDeliveryAddressModalOpen(true);
                }
              }}
            >
              <FormattedMessage
                id={
                  isBillingAddressMode
                    ? 'createNewBillingAddressTitle'
                    : 'createNewDeliveryAddressTitle'
                }
              />
            </Button>
          </div>
        }
      >
        <div className="px-1">
          {addresses?.length > 0 ? (
            addresses?.map((address, index) => {
              const descriptor = [
                address.companyName ||
                  [address.firstName, address.lastName].join(' '),
                address.line1,
                address.line2,
                [address.zip, address.city].join(' '),
              ]
                .filter((part) => !!part)
                .join(', ');

              const onRemoveClick = () => {
                setRemoveAddress(address);
                closeModal();
              };

              const onEditClick = () => {
                if (isBillingAddressMode) {
                  setEditBillingAddressModalData({
                    billingAddress: address,
                    onSuccess: () => setIsListAddressesModalOpen(true),
                  });
                  setIsEditBillingAddressModalOpen(true);
                } else {
                  setEditDeliveryAddressModalData({
                    deliveryAddress: address,
                    onSuccess: () => setIsListAddressesModalOpen(true),
                  });
                  setIsEditDeliveryAddressModalOpen(true);
                }
              };

              return (
                <>
                  <div className="d-flex align-items-center justify-content-between px-1">
                    <div className="flex-grow-1 text-truncate pr-2">
                      {descriptor}
                    </div>
                    <div className="d-flex align-items-center">
                      <span onClick={onEditClick}>
                        <Icon.Edit size={22} className="secondary mr-2" />
                      </span>
                      <span onClick={onRemoveClick}>
                        <Icon.Trash2 size={22} className="danger" />
                      </span>
                    </div>
                  </div>
                  {index < addresses.length - 1 && <hr />}
                </>
              );
            })
          ) : (
            <Empty
              className="my-2"
              titleId={
                isBillingAddressMode
                  ? 'emptyBillingAddressesListTitle'
                  : 'emptyDeliveryAddressesListTitle'
              }
            />
          )}
        </div>
      </ModalSheet>
      <RemoveConfirmationModal
        isOpen={!!removeAddress}
        closeModal={() => setRemoveAddress(null)}
        messageId={
          isBillingAddressMode
            ? 'removeBillingAddressTitle'
            : 'removeDeliveryAddressTitle'
        }
        titleId={
          isBillingAddressMode
            ? 'removeBillingAddressConfirmation'
            : 'removeDeliveryAddressConfirmation'
        }
        handleRemoval={() => {
          _removeAddress(removeAddress)
            .then(() =>
              isBillingAddressMode
                ? fetchBillingAddresses()
                : fetchDeliveryAddresses(),
            )
            .then(() => setIsListAddressesModalOpen(true));
        }}
      />
    </>
  );
};

ListAddressesModal.propTypes = {
  isOpen: PropTypes.bool,
  data: PropTypes.object,
  setIsListAddressesModalOpen: PropTypes.func,
  setIsCreateBillingAddressModalOpen: PropTypes.func,
  setCreateBillingAddressModalData: PropTypes.func,
  setIsEditBillingAddressModalOpen: PropTypes.func,
  setEditBillingAddressModalData: PropTypes.func,
  setIsCreateDeliveryAddressModalOpen: PropTypes.func,
  setCreateDeliveryAddressModalData: PropTypes.func,
  setIsEditDeliveryAddressModalOpen: PropTypes.func,
  setEditDeliveryAddressModalData: PropTypes.func,
  fetchBillingAddresses: PropTypes.func,
  fetchDeliveryAddresses: PropTypes.func,
};

const mapStateToProps = (state) => ({
  isOpen: state.modals.isListAddressesModalOpen,
  data: state.modals.listAddressesModalData,
  deliveryAddresses: state.me.deliveryAddresses,
  billingAddresses: state.me.billingAddresses,
});

export default connect(mapStateToProps, {
  setIsListAddressesModalOpen,
  setIsCreateBillingAddressModalOpen,
  setCreateBillingAddressModalData,
  setIsEditBillingAddressModalOpen,
  setEditBillingAddressModalData,
  setIsCreateDeliveryAddressModalOpen,
  setCreateDeliveryAddressModalData,
  setIsEditDeliveryAddressModalOpen,
  setEditDeliveryAddressModalData,
  fetchBillingAddresses,
  fetchDeliveryAddresses,
})(ListAddressesModal);
